import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import resourcesToBackend from 'i18next-resources-to-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * i18n initialization module — configures i18next for the entire application.
 *
 * Responsibilities:
 * - Defines the list of translation **namespaces** (feature-based JSON files)
 *   bundled at build-time from `src/locales/{lang}/{ns}.json` and shipped
 *   as hashed webpack chunks (cache-busted in lockstep with the JS).
 * - Declares the set of **supported languages** (it, en, fr, de, es, pt, pl).
 * - Configures **language detection** via `i18next-browser-languagedetector`
 *   which reads/writes the user's preference to `localStorage`.
 * - Sets Italian (`it`) as the **fallback language** so that any missing
 *   translation key gracefully degrades to the Italian value.
 * - Enables **missing-key logging** in development mode to surface
 *   untranslated strings early during development.
 *
 * @userstory US-I18N-01  — Platform displayed in user's preferred language
 * @userstory US-I18N-03  — Language preference persisted (LanguageDetector → localStorage)
 * @userstory US-I18N-06  — Fallback to Italian for missing translations
 * @userstory US-I18N-07  — Namespace-based file organisation (small, focused files)
 * @userstory US-I18N-08  — Missing-key detection in development mode
 * @userstory US-I18N-10  — Adding a new language only requires updating SUPPORTED_LANGUAGES
 *
 * @module i18n
 */

/**
 * Supported namespaces for translations.
 * Each namespace corresponds to a JSON file in `src/locales/{lang}/`.
 *
 * @userstory US-I18N-07 — Feature-based file organisation
 * @userstory US-I18N-11 — Small focused files for translators
 */
export const NAMESPACES = [
	'common',
	'menu',
	'dashboard',
	'organization',
	'projects',
	'publicProjects', // Public projects (company-published + talent expressions of interest)
	'talents',
	'team',
	'invoices',
	'timesheets',
	'messages',
	'profile',
	'settings',
	'billing',
	'legal',
	'subscription',
	'features',
	'validation',
	'errors',
	'quotes',
	'alliance', // Alliance/DreamTeam feature
	'onboarding', // Onboarding wizard (company + talent)
	'talentCollections', // Talent Collections (bookmarks, collections, notes)
	'explore', // Explore Talents feature (feed, preferences, AI assistant)
	'searches', // Search-centric company experience
	'report', // Platform reports feature
	'entitlements', // Entitlement labels (capabilities/quotas) for plan cards & usage
] as const;

export type Namespace = (typeof NAMESPACES)[number];

/**
 * Supported languages in the platform.
 * Adding a new language entry here (together with the corresponding
 * locale JSON files and a `LANG` entry in `lang.ts`) is all that is
 * needed to enable a new language — no other code changes required.
 *
 * @userstory US-I18N-10 — Adding new languages easily
 */
export const SUPPORTED_LANGUAGES = ['it', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const FALLBACK_LANGUAGE: SupportedLanguage = 'it';
export const LANGUAGE_STORAGE_KEY = 'i18nextLng';

export const normalizeLanguage = (language?: string | null): SupportedLanguage => {
	const normalizedLanguage = language?.trim().toLowerCase();
	if (!normalizedLanguage) return FALLBACK_LANGUAGE;

	if ((SUPPORTED_LANGUAGES as readonly string[]).includes(normalizedLanguage)) {
		return normalizedLanguage as SupportedLanguage;
	}

	const baseLanguage = normalizedLanguage.split(/[-_]/)[0];
	if ((SUPPORTED_LANGUAGES as readonly string[]).includes(baseLanguage)) {
		return baseLanguage as SupportedLanguage;
	}

	return FALLBACK_LANGUAGE;
};

i18n
	// Bundle translation JSONs as hashed webpack chunks (one per [lng]/[ns])
	// so they are always cache-busted in lockstep with the JS that uses them.
	// @userstory US-I18N-07 — Namespace-based file organisation
	.use(
		resourcesToBackend(
			(language: string, namespace: string) =>
				import(`./locales/${language}/${namespace}.json`),
		),
	)
	// detect user language
	.use(LanguageDetector)
	// pass the i18n instance to react-i18next
	.use(initReactI18next)
	// init i18next
	// for all options read: https://www.i18next.com/overview/configuration-options
	.init({
		// Language configuration
		// @userstory US-I18N-06 — Italian fallback ensures no untranslated content
		fallbackLng: FALLBACK_LANGUAGE,
		supportedLngs: [...SUPPORTED_LANGUAGES],
		load: 'languageOnly',
		cleanCode: true,

		detection: {
			order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
			caches: ['localStorage'],
			lookupQuerystring: 'lng',
			lookupLocalStorage: LANGUAGE_STORAGE_KEY,
			convertDetectedLanguage: normalizeLanguage,
		},

		// Debug mode in development
		debug: process.env.NODE_ENV === 'development',

		// Namespace configuration
		ns: NAMESPACES as unknown as string[],
		defaultNS: 'common',
		fallbackNS: 'common', // Fallback to common namespace

		interpolation: {
			escapeValue: false, // not needed for react as it escapes by default
		},

		react: {
			useSuspense: true,
			bindI18n: 'languageChanged loaded',
		},

		// Missing key handling — always active so production telemetry can pick it up.
		// In production the handler is wired in src/index.tsx to flush to App Insights.
		// @userstory US-I18N-08 — Logs missing translation keys so developers catch them before deployment
		saveMissing: true,
		missingKeyHandler: (lngs, ns, key) => {
			if (process.env.NODE_ENV === 'development') {
				console.warn(
					`[i18n] Missing translation: ${ns}:${key} for languages: ${lngs.join(', ')}`,
				);
			}
			// Production telemetry hook: dispatch a CustomEvent that src/index.tsx
			// (or any subscriber such as App Insights) can listen to without
			// coupling this module to the telemetry SDK.
			if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
				try {
					window.dispatchEvent(
						new CustomEvent('i18n:missing-key', {
							detail: { lngs, ns, key },
						}),
					);
				} catch {
					/* no-op */
				}
			}
		},
	});

export default i18n;
